import { ArrowLeft, ExternalLink, Lock, LockOpen } from "lucide-react";
import { money, vehicleLabel, workLabel } from "./advisorUtils";

const API = process.env.REACT_APP_BACKEND_URL;

// Hand-the-iPad-over view. Light panel on purpose — the dark advisor UI stays
// clearly "staff side", this reads as the customer's own page. Big type, no
// jargon, no internal buttons. `locked` = Handover mode: every staff-side
// control disappears until the advisor enters their code.
export const AdvisorCustomerView = ({ job, onBack, locked, onLock, onUnlockRequest }) => {
  const hasVideo = job.video_path && job.video_path.length > 2 && job.video_path !== "x";
  const videoSrc = hasVideo
    ? `${API}/api/files/${job.video_path}?share=${encodeURIComponent(job.share_token)}`
    : null;
  const items = (job.items || []).filter((i) => (i.unit_price || 0) > 0 || i.urgency !== "green");

  return (
    <div className={`h-full overflow-auto bg-zinc-100 text-zinc-900 ${locked ? "select-none" : ""}`} data-testid="advisor-customer-view" data-locked={locked ? "true" : "false"}>
      <div className="sticky top-0 z-10 bg-zinc-900 text-white px-5 py-3 flex items-center justify-between gap-4">
        {locked ? (
          <>
            <button
              type="button" onClick={onUnlockRequest}
              className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300"
              data-testid="advisor-handover-unlock-btn"
            >
              <Lock className="w-3.5 h-3.5" /> Handover mode
            </button>
            <span className="text-sm font-bold text-zinc-400">Your service today</span>
          </>
        ) : (
          <>
            <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm font-bold text-zinc-300 hover:text-white" data-testid="advisor-customer-back">
              <ArrowLeft className="w-4 h-4" /> Back to my desk
            </button>
            <div className="flex items-center gap-4">
              <button
                type="button" onClick={onLock}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 text-slate-950 text-sm font-black hover:bg-emerald-400"
                data-testid="advisor-handover-lock-btn"
              >
                <LockOpen className="w-4 h-4" /> Lock for handover
              </button>
              {job.share_token && (
                <a
                  href={`/j/${job.share_token}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-bold text-emerald-400 no-underline"
                  data-testid="advisor-customer-open-quote"
                >
                  Open their full quote <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </>
        )}
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="text-xs font-black uppercase tracking-[0.2em] text-zinc-500">Your vehicle today</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-1" data-testid="advisor-customer-reg">
          {job.vehicle_reg || "Your car"}
        </h1>
        <div className="text-lg text-zinc-600 mt-1">
          {vehicleLabel(job)} · {workLabel(job)}
        </div>

        {videoSrc && (
          <div className="mt-7 rounded-2xl overflow-hidden bg-black shadow-lg" data-testid="advisor-customer-video">
            <video src={videoSrc} controls playsInline className="w-full aspect-video" />
          </div>
        )}

        {job.ai_explanation && (
          <div className="mt-7 rounded-2xl bg-white border border-zinc-200 p-6 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-zinc-500 mb-2">What we found</div>
            <p className="text-base leading-relaxed text-zinc-700 whitespace-pre-line">{job.ai_explanation}</p>
          </div>
        )}

        <div className="mt-7 rounded-2xl bg-white border border-zinc-200 shadow-sm overflow-hidden" data-testid="advisor-customer-items">
          {items.length === 0 && (
            <div className="p-6 text-zinc-500">Nothing needing attention — your car passed its checks.</div>
          )}
          {items.map((it) => (
            <div key={it.id} className="px-6 py-5 border-b border-zinc-100 last:border-0 flex items-start gap-4">
              <span className={`mt-1.5 w-3 h-3 rounded-full shrink-0 ${
                it.urgency === "red" ? "bg-red-500" : it.urgency === "amber" ? "bg-amber-400" : "bg-emerald-500"
              }`} />
              <div className="min-w-0 flex-1">
                <div className="text-lg font-bold">{it.name}</div>
                {it.description && <div className="text-base text-zinc-600 mt-1 leading-relaxed">{it.description}</div>}
              </div>
              <div className="font-mono text-lg font-black tabular-nums shrink-0">
                {money((it.unit_price || 0) * (it.quantity || 1))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-7 rounded-2xl bg-zinc-900 text-white p-6 flex flex-wrap items-end justify-between gap-4" data-testid="advisor-customer-total">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.18em] text-zinc-400">You&apos;ve approved</div>
            <div className="font-mono text-3xl font-black text-emerald-400 tabular-nums">{money(job.approved_total)}</div>
          </div>
          <div className="text-right">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-zinc-400">Today&apos;s total</div>
            <div className="font-mono text-3xl font-black tabular-nums">{money(job.grand_total)}</div>
          </div>
        </div>

        <p className="text-sm text-zinc-500 mt-5">
          Nothing is done without your say-so. Everything above is priced, fitted and guaranteed.
        </p>
      </div>
    </div>
  );
};
